import { supabase } from './supabaseClient';
import type { AnimalGroup, IndividualAnimal, MedicalRecord } from '../types/animal';

const GROUPS_TABLE = 'animal_groups';
const ANIMALS_TABLE = 'animals';
const YIELDS_TABLE = 'animal_yields';
const MEDICAL_RECORDS_TABLE = 'animal_medical_records';
const AUDIT_LOGS_TABLE = 'audit_logs';

// Helper: camelCase to snake_case
const toSnake = (obj: any): any => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(toSnake);
  return Object.fromEntries(
    Object.entries(obj).map(([k, v]) => [
      k.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`),
      typeof v === 'object' && v !== null ? toSnake(v) : v
    ])
  );
};

// Helper: snake_case to camelCase
const toCamel = (obj: any): any => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(toCamel);
  return Object.fromEntries(
    Object.entries(obj).map(([k, v]) => [
      k.replace(/_([a-z])/g, (_, l) => l.toUpperCase()),
      typeof v === 'object' && v !== null ? toCamel(v) : v
    ])
  );
};

// Helper: Clean payload by converting empty strings to null
const cleanPayload = (obj: any): any => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(cleanPayload);
  return Object.fromEntries(
    Object.entries(obj).map(([k, v]) => {
      if (v === "") return [k, null];
      if (typeof v === 'object' && v !== null) return [k, cleanPayload(v)];
      return [k, v];
    })
  );
};

export const fetchAnimalGroups = async (farmId: string): Promise<any[]> => {
  const { data, error } = await supabase
    .from(GROUPS_TABLE)
    .select('*')
    .eq('farm_id', farmId);
  
  if (error) throw error;
  return (data || []).map(toCamel);
};

export const fetchAnimals = async (farmId: string): Promise<IndividualAnimal[]> => {
  const { data, error } = await supabase
    .from(ANIMALS_TABLE)
    .select(`
      *,
      yields: ${YIELDS_TABLE} (*)
    `)
    .eq('farm_id', farmId);
  
  if (error) throw error;
  
  return (data || []).map((item: any) => {
    const camelItem = toCamel(item);
    return {
      ...camelItem,
      tagId: item.tag_number,
      acquired: item.acquired_date,
      assetValue: item.asset_value,
      medicalRecords: camelItem.medicalRecords || [],
      yields: (item.yields || []).map(toCamel)
    };
  });
};

export const fetchMedicalRecords = async (animalId: string): Promise<MedicalRecord[]> => {
  const { data, error } = await supabase
    .from(MEDICAL_RECORDS_TABLE)
    .select('*')
    .eq('animal_id', animalId)
    .order('date', { ascending: false });
  
  if (error) throw error;
  return (data || []).map(toCamel);
};

export const fetchBulkMedicalRecords = async (groupId: string): Promise<MedicalRecord[]> => {
  const { data, error } = await supabase
    .from(MEDICAL_RECORDS_TABLE)
    .select('*')
    .eq('treatment_group', groupId)
    .eq('is_bulk_treatment', true)
    .order('date', { ascending: false });
  
  if (error) throw error;
  return (data || []).map(toCamel);
};

// --- Write Operations ---

export const deleteAnimalGroup = async (groupId: string): Promise<void> => {
  const { error } = await supabase
    .from(GROUPS_TABLE)
    .delete()
    .eq('id', groupId);
  if (error) throw error;
};

export const deleteAnimal = async (animalId: string): Promise<void> => {
  const { error } = await supabase
    .from(ANIMALS_TABLE)
    .delete()
    .eq('id', animalId);
  if (error) throw error;
};

export const createMedicalRecord = async (record: Partial<MedicalRecord>): Promise<MedicalRecord[]> => {
  const payload = toSnake(cleanPayload(record));
  if (!payload.id) delete payload.id;
  const { data, error } = await supabase
    .from(MEDICAL_RECORDS_TABLE)
    .insert([payload])
    .select();
  if (error) throw error;
  return (data || []).map(toCamel);
};

export const updateMedicalRecord = async (record: Partial<MedicalRecord>): Promise<void> => {
  const payload = toSnake(cleanPayload(record));
  const id = record.id;
  delete payload.id; // Ensure ID is not in the update payload
  
  const { error } = await supabase
    .from(MEDICAL_RECORDS_TABLE)
    .update(payload)
    .eq('id', id);
  if (error) throw error;
};

export const deleteMedicalRecord = async (recordId: string): Promise<void> => {
  const { error } = await supabase
    .from(MEDICAL_RECORDS_TABLE)
    .delete()
    .eq('id', recordId);
  if (error) throw error;
};

export const createAuditLog = async (log: any): Promise<void> => {
  const payload = toSnake(cleanPayload(log));
  if (!payload.id) delete payload.id;
  const { error } = await supabase.from(AUDIT_LOGS_TABLE).insert([payload]);
  if (error) throw error;
};

// Helper for animal payload mapping
const toAnimalPayload = (animal: Partial<IndividualAnimal>): any => {
  const cleaned = cleanPayload(animal);
  const payload = toSnake(cleaned);
  
  // Handle specific field mappings
  if (animal.tagId !== undefined) {
    payload.tag_number = animal.tagId || null;
    delete payload.tag_id;
  }
  
  if (animal.acquired !== undefined) {
    payload.acquired_date = animal.acquired || null;
    delete payload.acquired;
  }

  // Ensure ID is not sent if it's null or empty (let DB handle generation)
  if (!payload.id) {
    delete payload.id;
  }

  delete payload.medical_records;
  delete payload.yields; // Remove yields from main animal payload
  return payload;
};

export const createAnimal = async (animal: Partial<IndividualAnimal>): Promise<void> => {
  const payload = toAnimalPayload(animal);
  const yields = animal.yields || [];
  
  const { data, error } = await supabase.from(ANIMALS_TABLE).insert([payload]).select();
  if (error) throw error;
  
  if (data && data[0] && yields.length > 0) {
    const animalId = data[0].id;
    const yieldPayload = yields.map(y => toSnake({ ...y, animalId }));
    const { error: yieldError } = await supabase.from(YIELDS_TABLE).insert(yieldPayload);
    if (yieldError) throw yieldError;
  }
};

export const updateAnimal = async (id: string, updates: Partial<IndividualAnimal>): Promise<void> => {
  const payload = toAnimalPayload(updates);
  delete payload.id;
  
  const { error } = await supabase.from(ANIMALS_TABLE).update(payload).eq('id', id);
  if (error) throw error;
  
  if (updates.yields) {
    // Delete existing yields and re-insert new ones (simple strategy)
    const { error: deleteError } = await supabase.from(YIELDS_TABLE).delete().eq('animal_id', id);
    if (deleteError) throw deleteError;
    
    if (updates.yields.length > 0) {
      const yieldPayload = updates.yields.map(y => toSnake({ ...y, animalId: id }));
      const { error: yieldError } = await supabase.from(YIELDS_TABLE).insert(yieldPayload);
      if (yieldError) throw yieldError;
    }
  }
};

export const createAnimalGroup = async (group: any): Promise<any> => {
  const payload = toSnake(cleanPayload(group));
  if (!payload.id) delete payload.id;
  const { data, error } = await supabase.from(GROUPS_TABLE).insert([payload]).select();
  if (error) throw error;
  return (data || []).map(toCamel);
};

export const createBulkAnimals = async (animals: Partial<IndividualAnimal>[]): Promise<void> => {
  for (const animal of animals) {
    await createAnimal(animal);
  }
};

const generateUuid = () => {
  // Use Web Crypto if available, otherwise fallback to a simple UUID v4 polyfill
  try {
    // @ts-ignore
    if (globalThis.crypto && typeof globalThis.crypto.randomUUID === 'function') {
      // @ts-ignore
      return globalThis.crypto.randomUUID();
    }
  } catch (_) {}
  const s: string[] = [];
  const hex = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx';
  for (let i = 0; i < hex.length; i++) {
    const c = hex[i];
    if (c === 'x' || c === 'y') {
      const r = (Math.random() * 16) | 0;
      s.push(c === 'x' ? r.toString(16) : ((r & 0x3) | 0x8).toString(16));
    } else {
      s.push(c);
    }
  }
  return s.join('');
};

export const createBulkMedicalRecords = async (record: Partial<MedicalRecord>, animalIds: string[]): Promise<void> => {
  if (!animalIds.length) return;
  
  // Generate a proper UUID for group_record_id when missing
  const groupRecordId = record.group_record_id || generateUuid();
  
  const payload = animalIds.map(animalId => {
    const rec = { ...record };
    delete rec.id; // Ensure new ID is generated by DB
    return toSnake(cleanPayload({
      ...rec,
      animal_id: animalId,
      group_record_id: groupRecordId
    }));
  });

  const { error } = await supabase.from(MEDICAL_RECORDS_TABLE).insert(payload);
  if (error) throw error;
};

export const updateBulkMedicalRecords = async (record: Partial<MedicalRecord>): Promise<void> => {
  const groupRecordId = record.group_record_id;
  if (!groupRecordId) throw new Error("Missing group_record_id for bulk update");

  const payload = toSnake(cleanPayload(record));
  delete payload.id;
  delete payload.animal_id; // Don't update animal link
  delete payload.group_record_id; // Don't update the grouping ID

  const { error } = await supabase
    .from(MEDICAL_RECORDS_TABLE)
    .update(payload)
    .eq('group_record_id', groupRecordId);
    
  if (error) throw error;
};

export const deleteBulkMedicalRecords = async (groupRecordId: string): Promise<void> => {
  const { error } = await supabase
    .from(MEDICAL_RECORDS_TABLE)
    .delete()
    .eq('group_record_id', groupRecordId);
  if (error) throw error;
};

export const uploadAnimalPhotoToStorage = async (file: File, fileName: string): Promise<string | null> => {
  const bucket = (import.meta as any)?.env?.VITE_SUPABASE_BUCKET || 'farmershub';
  const { error } = await supabase.storage
    .from(bucket)
    .upload(fileName, file);
    
  if (error) {
    console.error('Error uploading file:', error);
    return null; // Do not block animal save if bucket is missing
  }
  
  const { data } = supabase.storage
    .from(bucket)
    .getPublicUrl(fileName);
    
  return data?.publicUrl || null;
};
